import store from "@/store";
import { deepCopy } from "@/common/utils/utils";
import { ROOM_NOTIFY_SETTING_ENUM } from '@/common/constants/room';
import { getStoredNotifySetting, getDefaultNotifyByChannelId, checkIsMute } from '@/common/notifySettings'

import { findChannelSync } from '_js/find-channel'
import Vue from "vue";
import EventBus from "./EventBus";
import Cache from '_lib/DataCache'

const DEFAULT_STATE = {
  "redCount": 0,
  "showRedPoint": false,
}
const DEFAULT_NOTIFY_SETTING = {
  notify_type: 2,
}

type unreadType = {
  last_read_message_id: string,
  redCount: number,
  showRedPoint: boolean,
  hideNum?: boolean,
  isMute?: boolean
}
type roomChannel = {
  room_id?: string,
  channel_id?: string,
  all_clear?: boolean,
  channel_list_ids?:  Array<string>,
}
type notifySettingResult = { 
  notify_type: number,
  parent_notify_type?: number,
  extend_type?: string,
  dnds?: { 
    until: number, 
    duration: number 
  },
  is_dnds?: boolean
  room_notify_set?: boolean
}
class Unread {
  unreadStateCache: any;
  constructor() {
    this.unreadStateCache = Vue.observable({'cache': {}})
  }
  // 请求/chatroom/v1/msg/read_states的时候初始化一次缓存，后续都从缓存取
  // 初始化的时候遍历所有的房间和频道，把未读消息数量和通知设置都缓存起来
  // 如果没有某个房间 or 频道的话，就代表是0
  // 当某个房间/频道的未读消息数量和通知设置变化的时候，也要手动更新缓存
  initUnreadStateCache() {
    let all_unread_states = store.getters.all_unread_states
    let res = {}
    for (let room_id in all_unread_states) {
      const room = {
        redCount: 0,
        showRedPoint: false,
      }
      for (let channel_id in all_unread_states[room_id]) {
        const obj = this.handleUnreadState({room_id, channel_id}, all_unread_states)
        if (obj.redCount || obj.showRedPoint || obj.isMute) {
          room[channel_id] = obj
        }
      }
      const { redCount, showRedPoint } = calculateRoomUnreadState(room, room_id)
      room.redCount = redCount
      room.showRedPoint = showRedPoint

      if (room && Object.keys(room).length > 0) {
        res[room_id] = room
      }
    }
    Vue.set(this.unreadStateCache, 'cache', res)
    EventBus.$emit("InitUnreadStateCache")
    EventBus.$emit("UpdateRoomUnreadRedPoint")
  }
  handleUnreadState (data: roomChannel, all_unread_states) {
    const { room_id, channel_id } = data
    if (room_id && channel_id) {
      let res: unreadType = {
        last_read_message_id: '',
        redCount: 0,
        showRedPoint: false
      }
      const room_unread_state = all_unread_states[room_id] || {}
      const channel_unread_state = room_unread_state[channel_id] || {}
      let { mention_count, message_count, last_read_message_id } = channel_unread_state

      let { notify_type } = this.getNotifySetting({room_id, channel_id})
      res.last_read_message_id = last_read_message_id
      if (ROOM_NOTIFY_SETTING_ENUM[notify_type] === 'all') { // 如果是全部消息都要通知 就只显示数字
        res.redCount = message_count || 0
        res.showRedPoint = false 
      } else if (ROOM_NOTIFY_SETTING_ENUM[notify_type] === 'only_at') {
        res.redCount = mention_count || 0
        res.showRedPoint = message_count > 0
      } else { // 设置为无通知
        res.showRedPoint = message_count > 0
        res.redCount = 0
        res.hideNum = true
      }
      if (checkIsMute(room_id) || checkIsMute(room_id, channel_id)) {
        res.redCount = 0
        res.showRedPoint = false
        res.isMute = true // 这里需要加个字段用于把该频道加到缓存里，不然后面更新免打扰的时候频道不会更新
      }
      return res
    }
    return JSON.parse(JSON.stringify(DEFAULT_STATE))
  }
  // 增量更新缓存
  updateUnreadStateCache(data: roomChannel, from_mute?: boolean) { 
    const { room_id, channel_id, all_clear, channel_list_ids } = data
    if (!room_id && !all_clear) return
    const cache = this.unreadStateCache['cache']
    if (all_clear) {
      for (let room_id in cache) {
        cache[room_id] = {
          redCount: 0,
          showRedPoint: false,
        }
        this.updateCurRoomCollapseData(room_id)
      }
    } else if (!channel_id) {  // 如果没有channel_id，就给房间的所有频道都更新
      ensureRoomInCache(cache, room_id)
      for (let key in cache[room_id]) {
        if ((key === 'redCount' || key === 'showRedPoint') && !from_mute) {
          continue;
        }
        const res = this.handleUnreadState({room_id, channel_id: key}, store.getters.all_unread_states)
        cache[room_id][key] = res
      }
      updateRoomAggregatedState(cache, room_id)

      this.updateCurRoomCollapseData(room_id)
    } else {
      ensureRoomInCache(cache, room_id)

      const need_clear_ids = (channel_list_ids && channel_list_ids.length > 0) ? channel_list_ids : [channel_id]
      const all_unread_states = store.getters.all_unread_states

      need_clear_ids.forEach(id => {
        const res = this.handleUnreadState({
          room_id,
          channel_id: id,
        }, all_unread_states)
        Vue.set(cache[room_id], id, res)
      });

      updateRoomAggregatedState(cache, room_id)

      this.updateCurRoomCollapseData(room_id, channel_id)
    }
    this.unreadStateCache['cache'] = cache

    EventBus.$emit("UpdateUnreadStateCache", room_id)
    EventBus.$emit("UpdateRoomUnreadRedPoint", room_id)
  }
  getUnreadState(data: roomChannel) {
    let { room_id, channel_id } = data
    const deepCopyDefault = deepCopy(DEFAULT_STATE)
    if (!room_id) return deepCopyDefault
    // 没有频道的时候，说明取的是房间的消息，返回频道累加的结果
    // redCount: 是所有频道的未读消息数量的累加
    // showRedPoint: 只要有一个频道的showRedPoint是true，就返回true
    const cache = this.unreadStateCache['cache']

    const room = cache[room_id] || {}
    if (!room) {
      return deepCopyDefault
    }

    if (!channel_id) {
      return {
        redCount: room.redCount || deepCopyDefault.redCount,
        showRedPoint: room.showRedPoint || deepCopyDefault.showRedPoint,
      }
    }
    return room[channel_id] || deepCopyDefault
  }
  getNotifySetting(data: roomChannel, is_extend_parent = true, use_room_notify_set = true) {
    let { room_id, channel_id } = data
    let res: notifySettingResult = {
      notify_type: 0,
      dnds: undefined
    }
    let room_notify_type = 0
    let room_default_notify_type = 0
    let room_notify_set = false
    let group_notify_type = 0
    let group_notify_setting = 0
    let extend_channel_notify_setting = 0
    let res_is_group = false
    let group_dnds = undefined
    let all_notify_settings = store.getters.all_notify_settings

    let room_notify_settings = all_notify_settings[room_id]
    // 获取房间的通知设置直接赋值
    if (!channel_id) {
      res.notify_type = room_notify_settings?.notify_type
      res.dnds = room_notify_settings?.dnds
    } else { // 获取分组或者频道的通知设置
      let room_s = room_notify_settings
      if (room_s) {
        // 记录父级房间的通知设置
        room_notify_type = room_s.notify_type
        room_notify_set = !!room_s.room_notify_set
        if(room_id == store.getters.cur_room_data?.room_id) {
          room_default_notify_type = store.getters.cur_room_data?.room_setting?.room_notify_type ?? room_notify_type ?? 1
        } else {
          room_default_notify_type = room_notify_type ?? 1
        }
        // res.dnds = room_s.dnds
        const room_s_channels = room_s.channels || []
        // 此时取默认通知设置，并且继承房间的设置
        if (room_s_channels.length === 0 || !findChannelSync(channel_id, room_s_channels, false, false)) {
          const default_setting = getStoredNotifySetting(room_id, channel_id)
          res.notify_type = default_setting.notify_type
        } else {
          const { channel: foundChannel, groupChannel, isGroup } = findChannelNotifySettings(channel_id, room_s.channels || [])

          if (foundChannel) {
            res_is_group = isGroup

            // 如果是分组内的频道，设置分组相关信息
            if (groupChannel) {
              group_notify_type = groupChannel.notify_type
              group_notify_setting = groupChannel.channel_notify_setting
              group_dnds = groupChannel.dnds
            }

            // 如果用户手动设置了notify_type，则直接取设置
            if(foundChannel.notify_type !== undefined && foundChannel.notify_type !== 0) {
              res.notify_type = foundChannel.notify_type
            }
            extend_channel_notify_setting = foundChannel.channel_notify_setting || 0
            res.dnds = foundChannel.dnds

            // 用户没有手动设置时使用默认设置
            if (res.notify_type === undefined) {
              const defaultNotify = getDefaultNotifyByChannelId(room_id, channel_id)
              res.notify_type = defaultNotify.notify_type
            }
          }
        }
      }
    }
    // 如果需要继承父级，赋值为父级通知设置
    // 总体是手动 > 默认，用户自己的频道设置>用户自己的分组设置>用户自己的房间设置>房间默认的频道设置>房间默认的分组设置>房间默认的房间设置
    if (is_extend_parent) {
      if (res.notify_type === ROOM_NOTIFY_SETTING_ENUM['extend']) {
        if (group_notify_type) {
          res.notify_type = group_notify_type
        } else if (use_room_notify_set && room_notify_set) {
          res.notify_type = room_notify_type
        } else if (extend_channel_notify_setting) {
          res.notify_type = extend_channel_notify_setting
        } else if (group_notify_setting) {
          res.notify_type = group_notify_setting
        } else {
          res.notify_type = room_default_notify_type
        }
      }
    }
    // 添加父级信息，通知设置中使用
    if (group_notify_type) {
      res.parent_notify_type = group_notify_type
      res.extend_type = '分组'
    } else if (use_room_notify_set && room_notify_set) {
      res.parent_notify_type = room_notify_type
      res.extend_type = '房间'
      res.room_notify_set = true
    } else if(extend_channel_notify_setting) {
      res.extend_type = res_is_group ? '分组': '频道'
      res.parent_notify_type = extend_channel_notify_setting
    } else if(group_notify_setting) {
      res.extend_type = '分组'
      res.parent_notify_type = group_notify_setting
    } else {
      res.parent_notify_type = room_default_notify_type
      res.extend_type = '房间'
    }

    const room_dnds = room_notify_settings?.dnds
    if (isDnds(res.dnds) || isDnds(group_dnds) || isDnds(room_dnds)) {
      res.is_dnds = true
    }

    return res || DEFAULT_NOTIFY_SETTING
  }
  // 获取当前channel_id对应的通知设置
  findChannelNotify(channel_id = '', channels = []) {
    for (let group of channels) {
      if (group.channel_id === channel_id) {
        return group
      }
      if (group.channel_list) {
        let channel = group.channel_list.find(
          (channel) => channel.channel_id === channel_id
        )
        if (channel) {
          return channel
        }
      }
    }
  }

  // 更新当前房间缓存数据的未读状态
  async updateCurRoomCollapseData(room_id: string, channel_id: string = ''){
    const roomCache = await Cache.getRoomCache(room_id)
    if(roomCache){
      // 如果有频道ID，则更新该频道
      const hideGroupIds = store.getters.hide_channel_group_ids[room_id] || []

      if(channel_id){
        const channelData = findChannelSync(channel_id, roomCache.channels)
        if(channelData && channelData.parent_id){
          if(hideGroupIds && hideGroupIds.length && hideGroupIds.includes(channelData.parent_id)){
            EventBus.$emit('RoomCache:Update_Collapse_Data', { room_id, channel_id })
          }
        }
      // 如果没有频道则更新全部
      } else {
        for(let i = 0; i < hideGroupIds.length; i++){
          const hGroup = hideGroupIds[i]
          const group = findChannelSync(hGroup, roomCache.channels)
          if(group){
            EventBus.$emit('RoomCache:Update_Collapse_Data', { room_id: room_id, channel_id: group.channel_id }, true)
          }
        }
      }
    }
  }
}

/**
 * 累加房间内所有频道的未读状态
 * @param roomData 房间缓存数据
 * @returns 累加后的未读状态
 */
function calculateRoomUnreadState(roomData: any, roomId: string) {
  let redCount = 0;
  let showRedPoint = false;

  for (let key in roomData) {
    if (key === 'redCount' || key === 'showRedPoint') {
      continue;
    }
    const obj = roomData[key];
    if (typeof obj === 'object' && obj.redCount !== undefined) {
      redCount += obj.redCount;
      showRedPoint = showRedPoint || obj.showRedPoint;
    }
  }

  if (checkIsMute(roomId)) {
    redCount = 0
    showRedPoint = false
  }
  return { redCount, showRedPoint };
}

/**
 * 更新房间缓存中的累加状态
 * @param cache 缓存对象
 * @param roomId 房间ID
 */
function updateRoomAggregatedState(cache: any, roomId: string) {
  const { redCount, showRedPoint } = calculateRoomUnreadState(cache[roomId], roomId);
  Vue.set(cache[roomId], 'redCount', redCount);
  Vue.set(cache[roomId], 'showRedPoint', showRedPoint);
}

/**
 * 确保房间在缓存中存在
 * @param cache 缓存对象
 * @param roomId 房间ID
 */
function ensureRoomInCache(cache: any, roomId: string) {
  if (!cache[roomId]) {
    Vue.set(cache, roomId, {});
  }
}

/**
 * 在频道列表中查找指定频道的通知设置
 * @param channelId 频道ID
 * @param channels 频道列表
 * @returns 查找结果包含频道设置和父级信息
 */
function findChannelNotifySettings(channelId: string, channels: any[]) {
  let result = {
    channel: null,
    groupChannel: null,
    isGroup: false
  };

  for (let channel of channels) {
    if (channel.channel_id === channelId) {
      result.channel = channel;
      result.isGroup = !!channel.channel_list;
      return result;
    }

    if (channel.channel_list) {
      for (let c of channel.channel_list) {
        if (c.channel_id === channelId) {
          result.channel = c;
          result.groupChannel = channel;
          result.isGroup = false;
          return result;
        }
      }
    }
  }

  return result;
}

function isDnds(dnds: any) {
  return dnds && (dnds.until > new Date().getTime() / 1000 || dnds.duration == -1)
}

const unread = new Unread()
export default unread;